import * as React from 'react';
import { CSSProperties, createElement as el } from 'react';
import { conditional } from './Declarative';
import { Size } from './Geometry';
import * as ViewportHelpers from './ViewportHelpers';

interface ViewportConstrainedDomProps {
  /** Whether the element should be forced to be the max allowable size. */
  isMaximized?: boolean;

  /**
   * NOTE 2016-11-29: The margin is the easy way out of dealing with scrollbar inconsistencies between desktop
   * and mobile.
   *
   * We want to size relative to the visual viewport but currently in the Chrome/Edge viewport model, only
   * window.innerWidth and window.innerHeight reflect the visual viewport size. The problem with these properties
   * is that they include the scrollbar width, so on browsers with scroll bars that occupy space our perceived
   * visual viewport can be larger than the real visual viewport, so larger elements are often positioned underneath
   * a scroll bar.
   *
   * The margin creates a guaranteed space between from the edge of the screen so that we won't position underneath
   * a scroll bar. The alternatives are browser/OS sniffing or hacky scrollbar width detection.
   */

  /** The amount of space that should be removed from the viewport width when constraining to the viewport size. */
  horizontalMargins?: number;

  /** The amount of space that should be removed from the viewport height when constraining to the viewport size. */
  verticalMargins?: number;

  /**
   * Styles to apply to the element. Setting max-width or max-height here will override any viewport-based size
   * constraints that this wrapper implements.
   */
  style?: CSSProperties;

  /**
   * Optional test id to be attached to the wrapper.
   */
  testId?: string;
}

interface ViewportConstrainedDomState {
  visualViewportSize: Size;
  layoutViewportSize: Size;
}

/**
 * Wraps a component in a container that will size intelligently to its contents and the viewport. The size constraints
 * are determined once when the component is constructed and need to be manually updated with updateSize().
 *
 * The default behavior is to allow its children to determine the container size, but when
 * (a) zoomed out, restrict the container size to be less than the visual viewport size or
 * (b) zoomed in/no zoom, restrict the container size to be less than the page's initial containing block
 */
class ViewportConstrainedDomWrapper extends React.Component<ViewportConstrainedDomProps, ViewportConstrainedDomState> {

  public constructor(props: ViewportConstrainedDomProps) {
    super(props);

    this.state = this.getStateForCurrentViewport();
  }

  private getStateForCurrentViewport(): ViewportConstrainedDomState {
    const visualViewport = ViewportHelpers.getVisualViewport();
    return {
      visualViewportSize: {
        width: visualViewport.width,
        height: visualViewport.height
      },
      layoutViewportSize: ViewportHelpers.getLayoutViewportSize()
    };
  }

  private getMaxSize(): Size {
    /**
     * Generally we want the element to be no larger than the visual viewport.
     * But when zoomed in, the contents may get squished as a result if we limit the size purely on the visual viewport.
     * If a min-width/min-height value is set on the wrapper we assume that this case has been handled, otherwise we
     * set a floor value on max-width/max-height.
     */
    const baseMaxSize = { width: this.state.visualViewportSize.width, height: this.state.visualViewportSize.height };

    const isMinWidthSpecified = this.props.style && this.props.style.minWidth;
    if (!isMinWidthSpecified) {
      baseMaxSize.width = Math.max(baseMaxSize.width, this.state.layoutViewportSize.width);
    }

    const isMinHeightSpecified = this.props.style && this.props.style.minHeight;
    if (!isMinHeightSpecified) {
      baseMaxSize.height = Math.max(baseMaxSize.height, this.state.layoutViewportSize.height);
    }

    if (this.props.horizontalMargins) {
      baseMaxSize.width -= this.props.horizontalMargins;
    }
    if (this.props.verticalMargins) {
      baseMaxSize.height -= this.props.verticalMargins;
    }

    return baseMaxSize;
  }

  /**
   * Updates size so that the size constraints reflect the current viewport size. Triggers a re-render.
   */
  public updateSize(): void {
    this.setState(this.getStateForCurrentViewport());
  }

  public render(): JSX.Element {
    const maxSize = this.getMaxSize();

    const divProps = {
      style: {
        maxWidth: maxSize.width, maxHeight: maxSize.height,
        ...this.props.style,
        ...conditional(this.props.isMaximized, { width: maxSize.width, height: maxSize.height })
      },
      'data-tb-test-id': this.props.testId
    };

    return el('div', divProps, this.props.children);
  }
}

export {
  ViewportConstrainedDomProps,
  ViewportConstrainedDomState,
  ViewportConstrainedDomWrapper
};
